/**
 * JudgmentInsightPanel — 5-panel visualization of the /api/judgment-law/timeline
 * response for a single judgment. Rendered:
 *   1. Standard onboarding — as a full-width mid-flow step between the
 *      Judgment substeps (Substep 3) and the Broker Preferences (Substep 4).
 *   2. Debtor Profile OverviewTab — as a summary block at the top.
 *
 * Requires props:
 *    - `payload` shape:
 *         { state, date_entered, court_level_variant?, lien_filed?,
 *           lien_filed_at?, known_real_property_states? }
 *    - `debtorName`, `judgmentAmountLabel` (optional strings for the header).
 */
import React from "react";
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  FileText,
  Info,
  LandPlot,
  MapPin,
  ShieldAlert,
  ShieldCheck,
  Skull,
} from "lucide-react";
import { useJudgmentTimeline } from "@/hooks/useJudgmentLaw";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import JeaCard from "@/components/jea/JeaCard";
import { RENEWAL_MODE, resolveRenewalDisplay, NO_PATHWAY_NEUTRAL_MESSAGE } from "@/lib/renewalDisplay";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
const daysToYearsMonths = (days) => {
  if (days == null) return "—";
  const abs = Math.abs(days);
  const years = Math.floor(abs / 365);
  const months = Math.floor((abs % 365) / 30);
  const parts = [];
  if (years) parts.push(`${years}y`);
  if (months) parts.push(`${months}m`);
  parts.push(`${abs % 30}d`);
  return (days < 0 ? "-" : "") + parts.join(" ");
};

const shortDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const StatusPill = ({ tone, label, testid }) => {
  const tones = {
    green: "bg-success/15 text-success border-success/40",
    yellow: "bg-warning/15 text-warning border-warning/40",
    red: "bg-danger/15 text-danger border-danger/40",
    grey: "bg-bg-tertiary text-text-muted border-border-default",
  };
  return (
    <span
      className={cn("inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold tracking-[0.08em] uppercase border", tones[tone] || tones.grey)}
      data-testid={testid}
    >
      {label}
    </span>
  );
};

const severityBg = {
  emergency: "bg-danger/20 border-danger/60 text-danger",
  critical: "bg-danger/15 border-danger/50 text-danger",
  urgent: "bg-warning/15 border-warning/50 text-warning",
  recommended: "bg-accent-pink/10 border-accent-pink/50 text-accent-pink",
  info: "bg-bg-tertiary border-border-default text-text-secondary",
};

const WarningRow = ({ w }) => (
  <div
    className={cn(
      "flex items-start gap-3 rounded-[8px] border p-3 text-[13px]",
      severityBg[w.severity] || severityBg.info
    )}
    data-testid={`insight-warning-${w.type}`}
  >
    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
    <div className="flex-1 min-w-0">
      <div className="font-bold uppercase tracking-[0.06em] text-[10px]">{w.severity}</div>
      <div className="text-white mt-0.5">{w.message}</div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Skeleton + error + empty
// ---------------------------------------------------------------------------
const InsightSkeleton = () => (
  <JeaCard className="min-h-[280px] animate-pulse" data-testid="insight-skeleton">
    <div className="h-6 w-1/2 rounded bg-bg-tertiary" />
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-24 rounded bg-bg-tertiary" />
      ))}
    </div>
  </JeaCard>
);

const InsightEmpty = ({ hint }) => (
  <JeaCard data-testid="insight-empty">
    <div className="flex items-start gap-3">
      <Info size={18} className="text-accent-pink shrink-0 mt-0.5" />
      <div className="text-[13px] text-text-secondary">
        {hint || "Choose a state and judgment entry date to see state-specific insights."}
      </div>
    </div>
  </JeaCard>
);

// ---------------------------------------------------------------------------
// Panel A — Judgment Status
// ---------------------------------------------------------------------------
const PanelA = ({ data }) => {
  if (!data.known) {
    return null;
  }
  const jd = data.dates?.judgment_expires || {};
  const daysUntil = jd.days_until;
  const isExpired = data.expired || (daysUntil != null && daysUntil < 0);
  const status = isExpired
    ? { tone: "grey", label: "Expired" }
    : daysUntil == null
    ? { tone: "grey", label: "Unknown" }
    : daysUntil <= 365
    ? { tone: "red", label: "Under 1 year" }
    : daysUntil <= 730
    ? { tone: "yellow", label: "1–2 years" }
    : { tone: "green", label: `${Math.floor(daysUntil / 365)}y+ remaining` };

  const entered = data.dates?.judgment_entered || {};
  const age = entered.days_until != null ? -entered.days_until : null;

  return (
    <JeaCard data-testid="insight-panel-a">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-accent-pink" />
          <div className="jea-caption">Judgment Status</div>
        </div>
        <StatusPill tone={status.tone} label={status.label} testid="insight-panel-a-status" />
      </div>
      <div className="grid grid-cols-2 gap-3 text-[13px]">
        <div>
          <div className="text-text-secondary">Age</div>
          <div className="text-white font-semibold jea-mono">{daysToYearsMonths(age)}</div>
        </div>
        <div>
          <div className="text-text-secondary">Duration ({data.name})</div>
          <div className="text-white font-semibold">{data.duration_note}</div>
        </div>
        <div>
          <div className="text-text-secondary">Time to expiration</div>
          <div className="text-white font-semibold jea-mono">
            {isExpired ? "Expired" : daysToYearsMonths(daysUntil)}
          </div>
        </div>
        <div>
          <div className="text-text-secondary">Expires</div>
          <div className="text-white font-semibold jea-mono">{shortDate(jd.iso)}</div>
        </div>
      </div>
    </JeaCard>
  );
};

// ---------------------------------------------------------------------------
// Panel B — Renewal or Revival Alert
// ---------------------------------------------------------------------------
const PanelB = ({ data }) => {
  // Phase 9G — when admin has marked the state NO_PATHWAY, render the neutral
  // panel instead of the renewal/revival detail block.
  if (resolveRenewalDisplay(data) === RENEWAL_MODE.NO_PATHWAY) {
    return (
      <JeaCard data-testid="insight-panel-b-no-pathway">
        <div className="flex items-center gap-2 mb-3">
          <Info size={16} className="text-text-secondary" />
          <div className="jea-caption">Renewal / Revival</div>
        </div>
        <div className="text-[13px] text-text-secondary leading-5">
          {NO_PATHWAY_NEUTRAL_MESSAGE}
        </div>
      </JeaCard>
    );
  }
  const rd = data.dates?.renewal_deadline || {};
  const dormancy = data.dates?.dormancy_trigger || {};
  const relevantWarnings = (data.warnings || []).filter((w) =>
    [
      "renewal_deadline",
      "dormancy_approaching",
      "dormant",
      "judgment_expiration",
      "judgment_expired",
      "hard_cutoff_approaching",
      "judgment_permanently_expired",
    ].includes(w.type)
  );
  return (
    <JeaCard data-testid="insight-panel-b">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert size={16} className="text-accent-pink" />
        <div className="jea-caption">Renewal / Revival</div>
      </div>
      <div className="text-[13px] space-y-2">
        <div>
          <div className="text-text-secondary">Mechanism</div>
          <div className="text-white font-semibold">{data.renewal_mechanism || "—"}</div>
        </div>
        {rd.iso && (
          <div>
            <div className="text-text-secondary">Suggested renewal deadline</div>
            <div className="text-white font-semibold jea-mono">
              {shortDate(rd.iso)} · {rd.days_until != null ? `${rd.days_until} days` : ""}
            </div>
          </div>
        )}
        {dormancy.iso && (
          <div>
            <div className="text-text-secondary">Dormancy trigger</div>
            <div className="text-white font-semibold jea-mono">
              {shortDate(dormancy.iso)} · {dormancy.days_until != null ? `${dormancy.days_until} days` : ""}
            </div>
          </div>
        )}
        {data.strategy_note && (
          <div className="pt-2 mt-2 border-t border-border-default text-[12px] text-text-secondary italic">
            {data.strategy_note}
          </div>
        )}
      </div>
      {relevantWarnings.length > 0 && (
        <div className="mt-3 space-y-2">
          {relevantWarnings.map((w, i) => <WarningRow key={i} w={w} />)}
        </div>
      )}
    </JeaCard>
  );
};

// ---------------------------------------------------------------------------
// Panel C — Real Property Lien Status
// ---------------------------------------------------------------------------
const PanelC = ({ data }) => {
  const le = data.dates?.lien_expires || {};
  const lienWarnings = (data.warnings || []).filter((w) =>
    ["lien_expiration", "lien_expired"].includes(w.type)
  );

  // State-specific info cards
  const key = data.variant_key;
  const infoCards = [];
  if (key === "NY" || key === "WI") {
    infoCards.push({
      title: "Re-docket required at 10-year mark",
      body: "Judgment survives 20 years but the lien is only 10 — re-docket at the 10-year mark or lose lien rights.",
    });
  }
  if (key === "FL") {
    infoCards.push({
      title: "Florida — Two 10-year tranches",
      body: "Re-record judgment lien within the first 10-year tranche to preserve the second. Homestead exemption is constitutional and broad.",
    });
  }
  if (key === "IA") {
    infoCards.push({
      title: "Iowa — Separate lien clock",
      body: "20-year action-on-judgment but lien is only 10 years — track both timelines separately.",
    });
  }
  if (key === "OH") {
    infoCards.push({
      title: "Ohio — File certificate with county recorder",
      body: "Court docket entry alone does NOT create the lien; file the certificate of judgment with the county recorder.",
    });
  }
  if (key === "TX") {
    infoCards.push({
      title: "Texas — County-by-county abstract filing",
      body: "No statewide lien. File abstracts in every county where the debtor may own real property.",
    });
  }
  if (key === "LA") {
    infoCards.push({
      title: "Louisiana — Parish mortgage records",
      body: "Record in the parish mortgage office, not the court docket. Louisiana liens do not attach otherwise.",
    });
  }
  if (key === "NC") {
    infoCards.push({
      title: "North Carolina — Lien does not carry to renewed judgment",
      body: "One new action permitted. The new judgment must be separately docketed — the lien does not automatically survive.",
    });
  }

  return (
    <JeaCard data-testid="insight-panel-c">
      <div className="flex items-center gap-2 mb-3">
        <LandPlot size={16} className="text-accent-pink" />
        <div className="jea-caption">Real Property Lien</div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-[13px] mb-3">
        <div>
          <div className="text-text-secondary">Lien duration</div>
          <div className="text-white font-semibold">{data.lien_life_note || "—"}</div>
        </div>
        <div>
          <div className="text-text-secondary">Lien filed</div>
          <div className="text-white font-semibold">{data.lien_filed ? "Yes" : "No / Unknown"}</div>
        </div>
        {data.lien_filed && le.iso && (
          <>
            <div>
              <div className="text-text-secondary">Lien expires</div>
              <div className="text-white font-semibold jea-mono">{shortDate(le.iso)}</div>
            </div>
            <div>
              <div className="text-text-secondary">Days until</div>
              <div className="text-white font-semibold jea-mono">{le.days_until != null ? le.days_until : "—"}</div>
            </div>
          </>
        )}
      </div>
      {infoCards.length > 0 && (
        <div className="space-y-2 mt-3">
          {infoCards.map((c) => (
            <div
              key={c.title}
              className="rounded-[8px] border border-accent-pink/40 bg-accent-pink/5 p-3 text-[12px]"
              data-testid={`insight-panel-c-info-${c.title.split(" ")[0].toLowerCase()}`}
            >
              <div className="text-accent-pink font-bold uppercase tracking-[0.06em] text-[10px]">{c.title}</div>
              <div className="text-text-secondary mt-1">{c.body}</div>
            </div>
          ))}
        </div>
      )}
      {lienWarnings.length > 0 && (
        <div className="mt-3 space-y-2">
          {lienWarnings.map((w, i) => <WarningRow key={i} w={w} />)}
        </div>
      )}
    </JeaCard>
  );
};

// ---------------------------------------------------------------------------
// Panel D — Exemption Alerts (FL / TX only)
// ---------------------------------------------------------------------------
const PanelD = ({ data }) => {
  const code = data.state_code;
  if (code !== "FL" && code !== "TX") return null;
  return (
    <JeaCard data-testid={`insight-panel-d-${code.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={16} className="text-warning" />
        <div className="jea-caption">Exemption Alert</div>
      </div>
      {code === "FL" && (
        <div className="text-[13px] text-text-secondary">
          <strong className="text-warning">Florida homestead protection is constitutional.</strong>{" "}
          A judgment lien does NOT attach to a debtor's homestead. Verify homestead
          status before assuming any lien has practical value against real property.
        </div>
      )}
      {code === "TX" && (
        <div className="text-[13px] text-text-secondary">
          <strong className="text-warning">Texas homestead exemption is broad and powerful.</strong>{" "}
          Homestead property is generally exempt from execution. Confirm homestead
          status before assuming lien enforceability.
        </div>
      )}
    </JeaCard>
  );
};

// ---------------------------------------------------------------------------
// Panel E — Multi-State Enforcement Summary (only if ≥ 2 known-property states)
// ---------------------------------------------------------------------------
const PanelE = ({ data, payload }) => {
  const knownStates = payload?.known_real_property_states || [];
  if (knownStates.length < 2) return null;
  // Master deadline list — for now sorted by name; the per-state deep-dive
  // arrives in Phase 9C when we compute a batch of timelines.
  const jurState = data.state_code;
  const orderedStates = [...knownStates].sort();
  return (
    <JeaCard data-testid="insight-panel-e">
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={16} className="text-accent-pink" />
        <div className="jea-caption">Multi-State Enforcement Summary</div>
      </div>
      <div className="text-[13px] text-text-secondary mb-3">
        Real property is located in <strong className="text-white">{orderedStates.length}</strong>{" "}
        state(s). Filing procedures + lien durations differ per state; use this list
        as a starting checklist:
      </div>
      <div className="grid grid-cols-2 gap-2">
        {orderedStates.map((s) => {
          const isJurisdictional = s === jurState;
          return (
            <div
              key={s}
              className={cn(
                "rounded-[8px] border p-3 text-[12px]",
                isJurisdictional
                  ? "border-accent-pink/60 bg-accent-pink/5"
                  : "border-border-default bg-bg-tertiary/60"
              )}
              data-testid={`insight-panel-e-state-${s}`}
            >
              <div className="text-white font-bold text-[13px]">{s}</div>
              <div className="text-text-secondary mt-1">
                {isJurisdictional
                  ? "Judgment state — primary lien clock applies."
                  : "Out-of-state — confirm domestication or sister-state filing."}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[12px] text-text-muted italic">
        Phase 9C will surface a per-state expiration timeline for each entry.
      </div>
    </JeaCard>
  );
};

// ---------------------------------------------------------------------------
// Header + hard-cutoff banner
// ---------------------------------------------------------------------------
const HardCutoffBanner = ({ data }) => {
  if (data.expired) {
    return (
      <div
        className="flex items-start gap-3 rounded-[12px] border border-danger/60 bg-danger/10 p-4"
        data-testid="insight-hard-warning"
      >
        <Skull size={22} className="text-danger shrink-0" />
        <div>
          <div className="text-white font-bold text-[15px]">
            This judgment is permanently expired.
          </div>
          <div className="text-text-secondary text-[13px] mt-1">
            {data.name} — this cannot be revived. Consult counsel; enforcement through this platform is not available.
          </div>
        </div>
      </div>
    );
  }
  if (data.hard_cutoff) {
    return (
      <div
        className="flex items-start gap-3 rounded-[12px] border border-danger/50 bg-danger/10 p-4"
        data-testid="insight-hard-warning"
      >
        <AlertTriangle size={22} className="text-danger shrink-0" />
        <div>
          <div className="text-white font-bold text-[15px]">Hard cutoff state — no renewal path.</div>
          <div className="text-text-secondary text-[13px] mt-1">
            {data.name}. Enforcement must complete within the statutory window; renewal or revival is not available.
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const JudgmentInsightPanel = ({
  payload,
  debtorName,
  judgmentAmountLabel,
  layout = "grid", // 'grid' | 'stack'
  testid = "judgment-insight-panel",
}) => {
  const { data, loading, error } = useJudgmentTimeline(payload);
  const { inPreview } = usePreviewGate();

  if (!payload?.state || !payload?.date_entered) {
    return <InsightEmpty />;
  }
  if (loading) return <InsightSkeleton />;
  if (error) {
    return (
      <JeaCard data-testid={`${testid}-error`}>
        <div className="text-danger text-[13px]">
          <AlertTriangle size={14} className="inline mr-2" />
          Couldn't load state-specific insights. Try again in a moment.
        </div>
      </JeaCard>
    );
  }
  if (!data) return <InsightEmpty />;
  if (!data.known) {
    return (
      <JeaCard data-testid={`${testid}-unknown`}>
        <div className="text-text-secondary text-[13px]">
          <Info size={14} className="inline mr-2 text-accent-pink" />
          {payload.state
            ? `We don't have detailed data for '${payload.state}' yet — general
              enforcement continues to work but state-specific timing is not
              available.`
            : "State not specified."}
        </div>
      </JeaCard>
    );
  }

  return (
    <div className="space-y-4" data-testid={testid}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="jea-caption">Insight Panel</div>
          <h2 className="text-white font-bold text-[22px]">
            {debtorName || "This judgment"}
          </h2>
          {judgmentAmountLabel && (
            <div className="jea-mono text-accent-pink font-bold text-[16px] mt-1">
              {judgmentAmountLabel}
            </div>
          )}
          {inPreview ? (
            <a
              href="/billing?open=change-plan&target=pro-monthly"
              className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.06em] bg-warning/15 text-warning border border-warning/40 hover:bg-warning/20 transition-colors"
              data-testid="alerts-badge-preview"
            >
              <Bell size={11} /> Alerts locked — Upgrade to activate
            </a>
          ) : (
            <div
              className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.06em] bg-success/15 text-success border border-success/40"
              data-testid="alerts-badge-on"
              title="Automated deadline monitoring is active. Alerts sweep every 6 hours."
            >
              <Bell size={11} /> Alerts On
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="jea-caption">Jurisdiction</div>
          <div className="text-white font-semibold text-[14px]">{data.name}</div>
          <a
            href={data.citation}
            target="_blank"
            rel="noreferrer"
            className="text-accent-pink hover:text-accent-pink-light text-[11px] inline-flex items-center gap-1"
            data-testid="insight-citation-link"
          >
            <FileText size={11} /> Statutory citation
          </a>
        </div>
      </div>

      <HardCutoffBanner data={data} />

      <div className={cn(layout === "grid" ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "space-y-4")}>
        <PanelA data={data} />
        <PanelB data={data} />
        <PanelC data={data} />
        <PanelD data={data} />
        <PanelE data={data} payload={payload} />
      </div>
    </div>
  );
};

export default JudgmentInsightPanel;
